import React, { useEffect, useRef } from "react";
import {
  OUTSIDE_FLOOR_BG,
  FLOOR_EDGE,
  FLOOR_BG,
  GRID_LINE,
} from "../../FloorPlanModal/utils/constants";

// Spacing of the placeholder grid drawn when a plane site has no floor-plan
// image, in FLOOR pixels (i.e. canvas pixels at zoom 1). It is drawn through
// the viewport below, so the grid stays glued to the floor under zoom/pan
// instead of sliding across it. Unchanged at zoom 1 / pan 0.
const GRID_FLOOR_PX = 50;
// Below this on-screen spacing (only reachable zoomed out) the grid is a
// hatch that hides the map or the off-site dots instead of a scale, so it is
// skipped.
const MIN_GRID_PX = 10;

const NO_PAN = { x: 0, y: 0 };

/**
 * What the grid canvas paints under the plan, as a pure decision (jsdom has
 * no 2D context to test the drawing itself):
 *   surround   -- the darker OUTSIDE_FLOOR_BG fill around the floor. Not
 *                 painted while the map is on, so the map shows all around.
 *   sheetAlpha -- the FLOOR_BG sheet a site with no plan image gets. It is
 *                 part of the 平面圖 layer: hidden with it, faded with it.
 *   grid       -- the grid lines; skipped when the 網格 layer is off, or once
 *                 zoomed out so far that they would be closer than MIN_GRID_PX.
 * With no map arguments this is exactly the pre-map paint.
 */
export const floorPaint = ({
  hasImage,
  mapVisible = false,
  planVisible = true,
  planOpacity = 1,
  gridVisible = true,
  zoom = 1,
}) => ({
  surround: !mapVisible,
  sheetAlpha: hasImage || !planVisible ? 0 : planOpacity,
  grid: gridVisible && GRID_FLOOR_PX * zoom >= MIN_GRID_PX,
});

// Anchored to the FLOOR, not to the screen: the spacing is scaled by zoom
// and the phase is offset by pan, so the grid slides and stretches with
// everything else instead of standing still while the site moves underneath
// it (which made it read as a fixed screen texture and told the operator
// nothing). At zoom 1 / pan 0 this is exactly the old every-50px grid.
// Continuing it across the surround is what gives the slack a sense of
// motion while panning.
const paintGrid = (ctx, { canvasSize, viewport, zoom, pan, grid }) => {
  ctx.strokeStyle = GRID_LINE;
  ctx.lineWidth = 0.5;

  const gridSize = GRID_FLOOR_PX * zoom;
  const phase = (offset) => ((offset % gridSize) + gridSize) % gridSize;

  const xEnd = grid ? canvasSize.width : -1;
  for (let x = phase(pan.x); x <= xEnd; x += gridSize) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, canvasSize.height);
    ctx.stroke();
  }
  // y-up: the grid rows are phased from the metre origin's row, not from
  // the floor box's top.
  const originY = viewport.project({ x: 0, y: 0 }).y;
  const yEnd = grid ? canvasSize.height : -1;
  for (let y = phase(originY); y <= yEnd; y += gridSize) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(canvasSize.width, y);
    ctx.stroke();
  }
};

// The GROUND: what the 平面圖 layer sits on and fades into. The surround
// (left out while the map is on, so the map is the ground), the no-image
// sheet (that site's 平面圖, faded with it) and the floor-anchored grid.
// Drawn BENEATH the plan image, so fading or hiding the image reveals this
// ground, exactly as on a site with no image.
const paintGround = (ctx, props) => {
  const { canvasSize, viewport } = props;
  const floorBox = viewport.floorRect;
  const paint = floorPaint(props);
  // The canvas fills its card, so unless the floor's aspect happens to
  // match the card's there is slack beside or below the plan. Painting it
  // as surrounding surface -- rather than leaving it blank -- is what
  // stops the plan reading as "stuck in a corner of an empty box".
  if (paint.surround) {
    ctx.fillStyle = OUTSIDE_FLOOR_BG;
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);
  }

  // A site with no uploaded plan keeps its lighter sheet, so the floor
  // still reads as the floor rather than as more surround.
  if (paint.sheetAlpha > 0) {
    ctx.globalAlpha = paint.sheetAlpha;
    ctx.fillStyle = FLOOR_BG;
    ctx.fillRect(floorBox.left, floorBox.top, floorBox.width, floorBox.height);
    ctx.globalAlpha = 1;
  }

  paintGrid(ctx, { ...props, grid: paint.grid });
};

// The floor's edge, drawn explicitly ABOVE the plan image. Without it the
// surround grid runs straight past the end of the site and reads as MORE
// site -- which on the live dashboard is a real misreading: out-of-bounds is
// a meaningful state there (see the confinement boundary_area), and a worker
// outside the floor is a thing an operator has to be able to see.
const paintEdge = (ctx, { viewport }) => {
  const floorBox = viewport.floorRect;
  ctx.strokeStyle = FLOOR_EDGE;
  ctx.lineWidth = 1;
  ctx.strokeRect(
    floorBox.left + 0.5,
    floorBox.top + 0.5,
    floorBox.width - 1,
    floorBox.height - 1,
  );
};

const PAINTERS = { ground: paintGround, edge: paintEdge };

/**
 * The plane floor's background, on canvases the size of the card. Each host
 * draws TWO: `layer="ground"` beneath the plan image (surround, no-image
 * sheet, grid) and `layer="edge"` above it (the floor edge). The live
 * dashboard and the plan editor both draw them, so the two canvases look the
 * same in every layer state, and the plan image always fades into the ground
 * (the map, or the surround and grid), never into the card's white.
 *
 * `plane` false (the live line/tunnel strips) keeps the canvas, cleared,
 * for its click. With `onClick` the canvas takes the pointer (the live
 * dashboard's click-to-deselect); without it the pointer passes through.
 */
const FloorPaint = ({
  canvasRef,
  canvasSize,
  viewport,
  zoom = 1,
  pan = NO_PAN,
  hasImage,
  mapVisible = false,
  planVisible = true,
  planOpacity = 1,
  gridVisible = true,
  plane = true,
  layer = "edge",
  onClick,
}) => {
  const ownRef = useRef(null);
  const ref = canvasRef || ownRef;

  useEffect(() => {
    const ctx = ref.current?.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
    if (!plane) return;
    PAINTERS[layer](ctx, {
      canvasSize,
      viewport,
      zoom,
      pan,
      hasImage,
      mapVisible,
      planVisible,
      planOpacity,
      gridVisible,
    });
  }, [
    ref,
    canvasSize,
    viewport,
    zoom,
    pan,
    hasImage,
    mapVisible,
    planVisible,
    planOpacity,
    gridVisible,
    plane,
    layer,
  ]);

  return (
    <canvas
      ref={ref}
      data-layer={layer}
      width={canvasSize.width}
      height={canvasSize.height}
      onClick={onClick}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        backgroundColor: "transparent",
        ...(onClick
          ? { cursor: "pointer", pointerEvents: "auto" }
          : { pointerEvents: "none" }),
      }}
    />
  );
};

export default FloorPaint;
